import { NgIf, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { Cart, CartLifeCycle, CartSelectors, OrderTypes } from '@next/core-lib/cart';
import { DropdownButtonComponent } from '@next/core-lib/components';
import { FeatureFlags, FeatureFlagService } from '@next/core-lib/featureflag';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, switchMap, take } from 'rxjs';
import { ShopFeatureFlags } from 'src/app/shared/shop-feature-flags';

import * as ProductActions from '../../store/actions';

@Component({
  selector: 'product-return-button',
  templateUrl: './return-button.component.html',
  styleUrls: ['./return-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, DropdownButtonComponent, MatButton, MatIcon, AsyncPipe, AgrI18nSharedModule],
})
export class ReturnButtonComponent implements OnInit {
  returnOrdersEnabled$: Observable<boolean> = new Observable<boolean>();
  openReturnCart$: Observable<Cart | undefined> = new Observable<Cart | undefined>();
  returnPackagingEnabled$: Observable<boolean> = new Observable<boolean>();

  readonly boundOpenReturnDialog = this.openReturnDialog.bind(this);
  readonly boundOpenReturnPackagingDialog = this.openReturnPackagingDialog.bind(this);

  dropdownOptions$ = this.localeService.locale$.pipe(
    map((_) => [
      {
        text: this.localeService.translate('app.return-full-product'),
        action: this.boundOpenReturnDialog,
      },
      {
        text: this.localeService.translate('app.start-packaging-return'),
        action: this.boundOpenReturnPackagingDialog,
      },
    ]),
  );
  private readonly hasCustomerRole$ = this.store.select(AuthorizationSelectors.hasCustomerRole);
  private readonly customer$ = this.store.select(UserSelectors.selectCustomer);
  private readonly carts$: Observable<Cart[]> = this.store.select(CartSelectors.selectCurrentOrderCarts);

  constructor(
    private readonly store: Store,
    private readonly featureFlagService: FeatureFlagService,
    private readonly localeService: LocaleService,
  ) {}

  ngOnInit(): void {
    const featureFlags$ = this.customer$.pipe(
      map((selectedCustomer) => selectedCustomer?.shipTos[0]?.salesAreas[0]?.salesOrganization),
      switchMap((salesOrganization) => this.featureFlagService.getFeatureFlagsForSalesArea(salesOrganization)),
    );

    this.returnPackagingEnabled$ = featureFlags$.pipe(map((featureFlags) => featureFlags.includes(ShopFeatureFlags.showReturnablePackagingDropdownButton)));

    const returnFeatureFlagEnabled$ = featureFlags$.pipe(map((featureFlags) => featureFlags.includes(FeatureFlags.returnOrdersEnabled)));

    this.returnOrdersEnabled$ = combineLatest(
      [this.hasCustomerRole$, returnFeatureFlagEnabled$],
      (hasCustomerRole, returnEnabled) => returnEnabled && !hasCustomerRole,
    );

    this.openReturnCart$ = this.carts$.pipe(
      map((carts) => carts.find((cart) => cart.type === OrderTypes.returnableOrder && cart.status !== CartLifeCycle.finalized)),
    );
  }

  openReturnDialog(): void {
    this.openReturnCart$.pipe(take(1)).subscribe((openReturnCart) => {
      this.store.dispatch(
        ProductActions.openReturnSearchDialog({
          cart: openReturnCart || undefined,
        }),
      );
    });
  }

  openReturnPackagingDialog(): void {
    this.store.dispatch(ProductActions.openReturnPackagingSearchDialog());
  }
}
